// Tier 2 baseRosters migration, Phase B: the real intended replacement for the baseRosters portion
// of App.jsx's App.jsx's watchDoc mechanism — currently:
//   watchDoc("baseRosters", setBaseRosters, d=>d.baseRosters||{})
// which subscribes to the single warriors/baseRosters document and reads its `baseRosters` map
// field (keyed by teamId). This module instead subscribes to the new top-level `baseRosters`
// collection (one document per team, doc ID = the team's own id — see
// scripts/migrate-baserosters.cjs) and reconstructs the exact same in-memory shape:
// {[teamId]: playersArray}. Every existing read site (`baseRosters[teamId]||[]`, `.length`,
// `.filter`, `.map`) keeps working unchanged, since the map's values are still plain arrays, not
// wrapped in a {players:[...]} envelope — that envelope only exists at the Firestore document
// level (one field, `players`, per Phase A), unwrapped here at this boundary.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one baseRosters/{teamId} document into the plain players array the app's in-memory
// baseRosters[teamId] value has always been. Player entries themselves are already slim
// ({id,name,jersey,positions,ageGroup}) as of Phase A / the Phase B write-site conversion — nothing
// to undo here, unlike players/teams' id-type normalization.
//
// `nodeId` is NOT carried through here, unlike every sibling *DocToRecord helper (players, teams,
// coaches, practiceGroups, practiceCancellations, draftRosters) — deliberately, not an oversight.
// Checked every consumer of baseRosters[teamId] in src/App.jsx (the base-roster editor's add/
// remove handlers, the roster-builder "BASE ROSTER AVAILABLE" count) and all of them treat the
// return value as a bare array (.length, .filter, .some, spread into a new array). Changing this to
// {players, nodeId} would break every one of them — spreading a plain object into an array literal
// throws (not iterable), and .length/.filter/.some on a plain object are undefined, which would
// silently hide the "BASE ROSTER AVAILABLE" banner rather than crash. There's also no actual gap to
// close: baseRosters is keyed by teamId, and teams (nodeId now included) already carries nodeId per
// team, so any future consumer can get "which node owns this base roster" via
// teams.find(t=>t.id===teamId)?.nodeId without this file needing to change at all.
export function baseRostersDocToRecord(data) {
  return data.players || [];
}

// Live-subscription replacement for watchDoc("baseRosters", setBaseRosters, ...) — same ergonomics
// (takes the setter, returns an unsubscribe function). Kept as the unscoped variant for coach/parent
// (neither has a node concept), matching baseRosters' read rule staying isAllowed()||isParent() —
// admin now uses watchBaseRostersCollectionForNodes below, mirroring teams/coaches.
export function watchBaseRostersCollection(setter) {
  return onSnapshot(collection(db, 'baseRosters'), (snap) => {
    const map = {};
    snap.docs.forEach((d) => { map[d.id] = baseRostersDocToRecord(d.data()); });
    setter(map);
  });
}

// Node-scoped variant (mirroring watchTeamsCollectionForNodes/watchCoachesCollectionForNodes
// exactly) — constrained via where('nodeId','in',nodeIds) to a caller-supplied permitted-node list.
// Used for the admin role only. baseRosters' READ rule is now node-scoped too (see firestore.rules):
// nodeIds is always called with permittedNodeIds (App.jsx), which is built as [myNodeId, ...every
// node whose ancestorIds contains myNodeId] — exactly the set of nodeId values for which the rule's
// nodeIsDescendantOrSelf(nodeId, myNodeId) check passes. So this query's result set and the rule's
// permitted set are identical by construction: this is now the real security boundary the read rule
// enforces, not just a UX narrowing on top of a broader grant. Same 30-value 'in' operator ceiling as
// the other ForNodes variants.
export function watchBaseRostersCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'baseRosters'), where('nodeId', 'in', nodeIds)), (snap) => {
    const map = {};
    snap.docs.forEach((d) => { map[d.id] = baseRostersDocToRecord(d.data()); });
    setter(map);
  });
}
